import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import MeterReading from '../MeterReading/MeterReading';
import LubeSales from '../LubeSales/LubeSales';
import CreditSale from '../CreditSale/CreditSale';
import DailyReceipt from '../DailyReceipt/DailyReceipt';
import BillReceipt from '../BillReceipt/BillReceipt';
import { useAuth } from '../../context/AuthContext';
import './Sale.css';

const SUB_TABS = [
  { id: 'SaleList', labelKey: 'sale.tabs.saleList' },
  { id: 'MeterReading', labelKey: 'sale.tabs.meterReading' },
  { id: 'LubeSales', labelKey: 'sale.tabs.lubeSales' },
  { id: 'CreditSale', labelKey: 'sale.tabs.creditSale' },
  { id: 'DailyReceipt', labelKey: 'sale.tabs.dailyReceipt' },
  { id: 'BillReceipt', labelKey: 'sale.tabs.billReceipt' },
  { id: 'SaleReturn', labelKey: 'sale.tabs.saleReturn' },
];

const EMPTY = { sale_date: today(), customer_id: '', customer_name: 'Cash', product_code: 'HSD', qty: '', rate: '' };

export default function Sale({ subPage = 'SaleList' }) {
  const { t: tr } = useTranslation();
  const { user, can, canAccess } = useAuth();
  const [tab, setTab] = useState(subPage);
  useEffect(() => {
    setTab(subPage);
    setShowForm(false);
    setFilter('');
    setEditRow(null);
  }, [subPage]);

  const hasSalesAccess =
    canAccess('sales') ||
    canAccess('meter_readings') ||
    canAccess('lube_sales') ||
    canAccess('credit_sales') ||
    canAccess('daily_receipts') ||
    canAccess('bill_receipts');

  const { data: customers } = useApi(() => api.customers.list(), []);
  const { data: products } = useApi(() => api.products.list(), []);
  const { data: sales, loading, error, reload } = useApi(() => api.sales.list(), []);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRow, setEditRow] = useState(null);   // row being edited
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);  // { type:'delete'|'cancel', row }

  const [returns, setReturns] = useState([]);

  if (!hasSalesAccess) {
    return (
      <div className="sale-page">
        <ErrBanner msg={tr('sale.accessDenied')} />
      </div>
    );
  }

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const isToday = (d) => d === today();
  const isAdmin = can('sales', 'approve');

  // Admins can edit/delete anything; Staff can only edit today's unpaid sales
  const canEdit = (s) => can('sales', 'update') && (isAdmin || isToday(s.sale_date));
  const canDelete = (s) => can('sales', 'delete') && (isAdmin || (!['Paid', 'Credit'].includes(s.status)));

  const handleSave = async () => {
    if (!form.qty || !form.rate) { notify('❌ ' + tr('sale.toasts.qtyRateRequired')); return; }
    setSaving(true);
    try {
      await api.sales.create({
        sale_date: form.sale_date,
        customer_id: form.customer_id || null,
        customer_name: form.customer_name || 'Cash',
        product_code: form.product_code,
        qty: parseFloat(form.qty),
        rate: parseFloat(form.rate),
      });
      notify('✅ ' + tr('sale.toasts.saved')); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    if (!editRow.qty || !editRow.rate) { notify('❌ ' + tr('sale.toasts.qtyRateRequired')); return; }
    setSaving(true);
    try {
      await api.sales.update(editRow.id, { qty: parseFloat(editRow.qty), rate: parseFloat(editRow.rate) });
      notify('✅ ' + tr('sale.toasts.updated')); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await api.sales.remove(confirm.row.id);
      notify('✅ ' + tr('sale.toasts.deleted')); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleCancel = async () => {
    if (!confirm) return;
    try {
      await api.sales.cancel(confirm.row.id);
      notify('✅ ' + tr('sale.toasts.cancelled')); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };
  const handleReturn = (sale) => {
    if (!window.confirm(tr('sale.confirmReturn', { bill: sale.bill_no, amount: fmtNum(sale.amount) }))) return;
    setReturns(prev => [...prev, { ...sale, return_date: today(), status: 'Return' }]);
    notify('✅ ' + tr('sale.toasts.returnRecorded'));
  };

  const normalSales = (sales || []);
  const returnedSales = (sales || []).filter(s => s.status === 'Return');
  const filtered = (tab === 'SaleList' ? normalSales : returnedSales)
    .filter(s => !filter ||
      s.bill_no?.toLowerCase().includes(filter.toLowerCase()) ||
      s.customer_name?.toLowerCase().includes(filter.toLowerCase())
    );
  const total = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="sale-page">
      <div className="page-header">
        <h2 className="page-title">{tr('sale.title')}</h2>
        {(tab === 'SaleList' || tab === 'SaleReturn') && (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* <input className="form-control" style={{width:200}} placeholder="Search bill / customer…"
              value={filter} onChange={e=>setFilter(e.target.value)}/> */}
            {tab === 'SaleList' && can('sales', 'create') && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>＋ {tr('sale.newSale')}</button>
            )}
          </div>
        )}
      </div>

      <div className="sub-tab-bar">
        {SUB_TABS
          .filter(t => {
            const tabPermissions = {
              SaleList: 'sales',
              MeterReading: 'meter_readings',
              LubeSales: 'lube_sales',
              CreditSale: 'credit_sales',
              DailyReceipt: 'daily_receipts',
              BillReceipt: 'bill_receipts',
              SaleReturn: 'sales'
            };

            return canAccess(tabPermissions[t.id]);
          })
          .map(t => (
            <button key={t.id} className={`sub-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => {
                setTab(t.id);
                setShowForm(false);
                setFilter('');
                setEditRow(null);
                window.location.hash = t.id; // Sync URL with sub-tab
              }}>
              {tr(t.labelKey)}
            </button>
          ))}
      </div>

      {msg && <div className="notify-bar">{msg}</div>}

      {/* ── NEW SALE FORM ── */}
      {tab === 'SaleList' && showForm && (
        <div className="section-card">
          <div className="section-header">{tr('sale.newSaleEntry')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.date')}</label>
              <input type="date" className="form-control" value={form.sale_date}
                onChange={e => setForm({ ...form, sale_date: e.target.value })} />
            </div>
            <div className="form-group"><label>{tr('common.customer')}</label>
              <select className="form-control" value={form.customer_id} onChange={e => {
                const c = (customers || []).find(x => String(x.id) === e.target.value);
                setForm({ ...form, customer_id: e.target.value, customer_name: c ? c.name : 'Cash' });
              }}>
                <option value="">{tr('sale.cash')}</option>
                {(customers || []).filter(c => c.type === 'Credit').map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group"><label>{tr('common.product')}</label>
              <select className="form-control" value={form.product_code} onChange={e => {
                const p = (products || []).find(x => x.code === e.target.value);
                setForm({ ...form, product_code: e.target.value, rate: p ? p.rate : '' });
              }}>
                {(products || []).map(p => (<option key={p.id} value={p.code}>{p.name}</option>))}
              </select>
            </div>
            <div className="form-group"><label>{tr('common.quantity')}</label>
              <input type="number" className="form-control" value={form.qty}
                onChange={e => setForm({ ...form, qty: e.target.value })} placeholder="0" />
            </div>
            <div className="form-group"><label>{tr('sale.rateRupees')}</label>
              <input type="number" className="form-control" value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })} placeholder="0.00" />
            </div>
            {form.qty && form.rate && (
              <div className="form-group balance-group">
                <label>{tr('common.amount')}</label>
                <div className="balance-value" style={{ color: 'var(--secondary)', background: '#f0fff4', borderColor: '#9ae6b4' }}>
                  ₹{fmtNum(parseFloat(form.qty) * parseFloat(form.rate))}
                </div>
              </div>
            )}
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? tr('common.saving') : '💾 ' + tr('common.save')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* ── INLINE EDIT FORM ── */}
      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('sale.editSale')} — {editRow.bill_no} — {fmtDate(editRow.sale_date)}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.quantity')}</label>
              <input type="number" className="form-control" value={editRow.qty}
                onChange={e => setEditRow({ ...editRow, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>{tr('sale.rateRupees')}</label>
              <input type="number" className="form-control" value={editRow.rate}
                onChange={e => setEditRow({ ...editRow, rate: e.target.value })} />
            </div>
            {editRow.qty && editRow.rate && (
              <div className="form-group balance-group"><label>{tr('common.amount')}</label>
                <div className="balance-value">₹{fmtNum(parseFloat(editRow.qty) * parseFloat(editRow.rate))}</div>
              </div>
            )}
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? tr('common.saving') : '💾 ' + tr('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}

      {tab === 'MeterReading' && <MeterReading />}
      {tab === 'LubeSales' && <LubeSales />}
      {tab === 'CreditSale' && <CreditSale />}
      {tab === 'DailyReceipt' && <DailyReceipt />}
      {tab === 'BillReceipt' && <BillReceipt />}

      {!loading && (tab === 'SaleList' || tab === 'SaleReturn') && (
        <div className="section-card">
          <div className="section-header">
            {tab === 'SaleList' ? tr('sale.saleRecords') : tr('sale.saleReturns')} — {tr('common.total')}: ₹{fmtNum(total)}
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th>
                <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                <th style={{ textAlign: 'left' }}>{tr('sale.billNo')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.customer')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.product')}</th>
                <th>{tr('common.qty')}</th>
                <th>{tr('sale.rateRupees')}</th>
                <th>{tr('sale.amountRupees')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.status')}</th>
                {tab === 'SaleList' && <th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>}
              </tr></thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id || s.bill_no + i}>
                    <td>{i + 1}</td>
                    <td>{fmtDate(s.sale_date || s.return_date)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>{s.bill_no}</td>
                    <td>{s.customer_name}</td>
                    <td><span className={`badge ${s.product_code === 'MS' ? 'badge-danger' : s.product_code === 'CNG' ? 'badge-success' : 'badge-info'}`}>{s.product_code}</span></td>
                    <td className="right-align">{s.qty}</td>
                    <td className="right-align">₹{fmtNum(s.rate)}</td>
                    <td className="right-align">₹{fmtNum(s.amount)}</td>
                    <td><span className={`badge ${s.status === 'Paid' ? 'badge-success' : s.status === 'Return' ? 'badge-danger' : 'badge-warning'}`}>{s.status}</span></td>
                    {tab === 'SaleList' && (
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {canEdit(s) && (
                          <button title={tr('common.edit')} className="btn btn-sm" style={btnEdit}
                            onClick={() => setEditRow({ ...s })}>✏️</button>
                        )}
                        {canDelete(s) && (
                          <button title={tr('common.delete')} className="btn btn-sm" style={btnDel}
                            onClick={() => setConfirm({ type: 'delete', row: s })}>🗑️</button>
                        )}
                        {isAdmin && ['Paid', 'Credit'].includes(s.status) && (
                          <button title={tr('sale.cancelReverse')} className="btn btn-sm" style={btnCancel}
                            onClick={() => setConfirm({ type: 'cancel', row: s })}>↩️</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                    {tab === 'SaleList' ? tr('sale.noSales') : tr('sale.noReturns')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      <ConfirmModal
        isOpen={confirm?.type === 'delete'}
        title={tr('sale.deleteTitle')}
        message={tr('sale.deleteConfirm', { bill: confirm?.row?.bill_no })}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
      {/* Cancel/Reverse confirm */}
      <ConfirmModal
        isOpen={confirm?.type === 'cancel'}
        title={tr('sale.cancelReverseTitle')}
        message={tr('sale.cancelConfirm', { bill: confirm?.row?.bill_no, status: confirm?.row?.status })}
        variant="warning"
        confirmLabel={tr('sale.reverse')}
        onConfirm={handleCancel}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
const btnCancel = { background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
